import { createMutationKeys } from '@lukemorales/query-key-factory'

import {
  createFilesUploadProgressReporter,
  s3Upload,
} from '@core/react-query/helpers'
import {
  APIAxiosInstance,
  headersWithAccessToken,
} from '@core/utils/api-axios-instance'
import { RequestTypes } from './requestTypes'
import { ParticipantEntityType } from '@core/modules/briefings'

export const briefingKeys = createMutationKeys('briefings', {
  collectSignatures: {
    mutationKey: null,
    mutationFn: async ({
      accessToken,
      offlineSubmittedOn,
      idempotencyKey,
      onSubmissionProgress,
      participants,
      proofFiles = [],
      ...otherPayload
    }: RequestTypes['collectBriefingSignatures']) => {
      const createFileUploadProgressReporter =
        createFilesUploadProgressReporter(
          [
            ...proofFiles,
            ...participants.flatMap(({ signature }) =>
              signature ? [signature] : [],
            ),
          ],
          onSubmissionProgress,
        )
      const proofKeys = await Promise.all(
        proofFiles.map(async (file) => {
          const key = await s3Upload({
            file,
            folderName: 'permit_briefing_proof',
            accessToken,
            onUploadProgress: createFileUploadProgressReporter?.(),
          })

          return key
        }),
      )

      const folderNameByEntityType: Record<ParticipantEntityType, string> = {
        PERMIT: 'permit_role_signature',
        CHECKLIST: 'checklist_role_signature',
      }

      const participantsWithSignatureKeys = await Promise.all(
        participants.map(async ({ signature, ...participant }) => {
          if (!signature) {
            return participant
          }

          const signatureKey = await s3Upload({
            file: signature,
            folderName:
              folderNameByEntityType[otherPayload.referenceObjectType],
            accessToken,
            onUploadProgress: createFileUploadProgressReporter?.(),
          })

          return {
            ...participant,
            signatureKey,
            justBriefed: true,
          }
        }),
      )

      const headers = {
        'X-OFFLINE-SUBMITTED-ON': offlineSubmittedOn,
        'X-IDEMPOTENCY-KEY': idempotencyKey,
        ...headersWithAccessToken({ accessToken }),
      }

      const { data } = await APIAxiosInstance.post(
        'briefings/sessions/collect-signatures',
        {
          ...otherPayload,
          proofKeys,
          participants: participantsWithSignatureKeys,
        },
        { headers },
      )
      onSubmissionProgress?.(1)
      return data
    },
  },

  createBriefingSession: {
    mutationKey: null,
    mutationFn: async ({
      participants,
      offlineSubmittedOn,
      idempotencyKey,
      onSubmissionProgress,
      ...otherPayload
    }: RequestTypes['createBriefingSession']) => {
      onSubmissionProgress?.(0.95)
      const { data } = await APIAxiosInstance.post(
        'briefings/sessions',
        {
          ...otherPayload,
          participants,
        },
        {
          headers: {
            'X-OFFLINE-SUBMITTED-ON': offlineSubmittedOn,
            'X-IDEMPOTENCY-KEY': idempotencyKey,
          },
        },
      )
      onSubmissionProgress?.(1)
      return data
    },
  },

  updateBriefingSession: {
    mutationKey: null,
    mutationFn: async ({
      briefingSessionId,
      offlineSubmittedOn,
      idempotencyKey,
      onSubmissionProgress,
      ...otherPayload
    }: RequestTypes['updateBriefingSession']) => {
      onSubmissionProgress?.(0.95)
      const { data } = await APIAxiosInstance.put(
        `briefings/sessions/${briefingSessionId}`,
        otherPayload,
        {
          headers: {
            'X-OFFLINE-SUBMITTED-ON': offlineSubmittedOn,
            'X-IDEMPOTENCY-KEY': idempotencyKey,
          },
        },
      )
      onSubmissionProgress?.(1)
      return data
    },
  },

  discardBriefingSession: {
    mutationKey: null,
    mutationFn: async ({
      briefingSessionId,
      ...otherPayload
    }: RequestTypes['discardBriefingSession']) => {
      const { data } = await APIAxiosInstance.put(
        `briefings/sessions/${briefingSessionId}/discard`,
        otherPayload,
      )
      return data
    },
  },
})
